import { useId, useState, type FC } from 'react';
import type { ShipmentDetailsDto } from '@/api';
import { Alert, Button, Dialog, Textarea } from '@/ui';

import { resolveApiErrorMessage } from '../../lib/apiError';
import { useCancelShipment } from './api/mutations';

/*
 * Отмена отгрузки - CancelModal макета. Причина обязательна
 * (order-lifecycle.md, «Отменено»); отмена необратима. Доступна, пока в 1С
 * нет заказа, - остальное отсекает сервер (409), а кнопку прячет карточка.
 */

export interface CancelShipmentDialogProps {
  shipmentId: string;
  shipmentNumber: number | string;
  onClose: () => void;
  onCancelled?: (shipment: ShipmentDetailsDto) => void;
}

export const CancelShipmentDialog: FC<CancelShipmentDialogProps> = ({
  shipmentId,
  shipmentNumber,
  onClose,
  onCancelled,
}) => {
  const reasonId = useId();
  const [reason, setReason] = useState('');
  const cancel = useCancelShipment();

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={`Отменить отгрузку № ${shipmentNumber}`}
      footer={
        <>
          <Button onClick={onClose}>Не отменять</Button>
          <Button
            variant="danger"
            disabled={reason.trim() === '' || cancel.isPending}
            onClick={() =>
              cancel.mutate(
                { path: { id: shipmentId }, body: { reason: reason.trim() } },
                {
                  onSuccess: (cancelled) => {
                    onCancelled?.(cancelled);
                    onClose();
                  },
                },
              )
            }
          >
            Отменить отгрузку
          </Button>
        </>
      }
    >
      <Alert tone="warn" className="mb-3.5">
        Отгрузка перейдёт в «Отменено» и попадёт в историю. Действие необратимо.
      </Alert>
      <div className="flex flex-col gap-1.5">
        <label htmlFor={reasonId} className="text-xs font-medium text-ink-700">
          Причина отмены
        </label>
        <Textarea
          id={reasonId}
          placeholder="Клиент отказался, пересмотр объёма..."
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
      {cancel.isError && (
        <Alert tone="error" className="mt-3.5">
          Не удалось отменить: {resolveApiErrorMessage(cancel.error)}
        </Alert>
      )}
    </Dialog>
  );
};
